import { useEffect, useRef } from 'react';
import {
  LayoutDashboard,
  Smartphone,
  ScanLine,
  CreditCard,
  Activity,
  Shield,
  Building2,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    icon: LayoutDashboard,
    title: 'Mi Dashboard',
    description: 'Tu centro de operaciones web. Gestiona clientes, paquetes, solicitudes de servicio y sucursales desde un solo lugar.',
    features: [
      'Clientes y destinatarios con búsqueda instantánea',
      'Etiquetas QR por artículo y facturas en PDF',
      'Aduana inteligente: el arancel oficial con tus precios por agencia',
      'Bandeja única con todos los chats de tus clientes',
      'Reportes y métricas operativas',
    ],
  },
  {
    icon: Smartphone,
    title: 'Mi Envío Cuba — la app de tus clientes',
    description: 'Conectada a tu agencia: tus clientes rastrean sus envíos, solicitan servicios y te escriben — sin costo de desarrollo para ti.',
    features: [
      'Rastreo en tiempo real, artículo por artículo',
      'Prueba de entrega con foto, firma y GPS',
      'Programar recogida y Casillero Envíos',
      'Consulta de Cargos aduanales antes de enviar',
      'Chat directo con tu agencia',
    ],
  },
  {
    icon: ScanLine,
    title: 'Mi Admin — almacén y reparto',
    description: 'Tu almacén escanea en masa, tus repartidores actualizan cada entrega y la confirman con foto, firma y GPS.',
    features: [
      'Escaneo de códigos QR',
      'Actualización masiva de estados en almacén',
      'Prueba de entrega: foto, firma del receptor y GPS',
      'Modo sin conexión: escanean y entregan sin señal, todo se sincroniza al reconectar',
    ],
  },
  {
    icon: CreditCard,
    title: 'Registro de Cobros',
    description: 'Registra lo que cobras en efectivo o Zelle, con control de saldo pendiente por paquete y recibo en PDF.',
    features: [
      'Registro de cobros con estados a color',
      'Control de saldo por paquete',
      'Facturas y recibos en PDF',
    ],
  },
];

const additionalFeatures = [
  {
    icon: Activity,
    title: 'Actualizaciones en tiempo real',
    description: 'Actualizaciones instantáneas en dashboard y app sin necesidad de refrescar.',
  },
  {
    icon: Shield,
    title: 'Seguridad de datos',
    description: 'Datos cifrados en reposo, permisos granulares por empleado y auditoría inalterable de cada cambio.',
  },
  {
    icon: Building2,
    title: 'Multi-sucursal',
    description: 'Administra múltiples ubicaciones bajo una sola cuenta con control independiente.',
  },
  {
    icon: CheckCircle,
    title: 'Soporte dedicado',
    description: 'El equipo MEC te acompaña en el onboarding y en cada etapa de crecimiento.',
  },
];

export function Servicios() {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
          },
        }
      );

      const serviceCards = servicesRef.current?.querySelectorAll('.service-card');
      if (serviceCards) {
        gsap.fromTo(
          serviceCards,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: servicesRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const featureCards = featuresRef.current?.querySelectorAll('.feature-item');
      if (featureCards) {
        gsap.fromTo(
          featureCards,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.08,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: featuresRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <main className="bg-bg-light">
      <SEO
        title="Plataforma MEC | Dashboard, Apps y Herramientas para Agencias de Envíos a Cuba"
        description="MEC ofrece el panel de gestión, la app para tus clientes y la app de repartidores — todo lo que tu agencia de envíos a Cuba necesita, en la nube."
        path="/servicios"
        keywords="software gestión courier Cuba, dashboard agencia envíos, app móvil agencia paquetes Cuba, sistema para agencia de paquetería a Cuba, herramientas agencia courier"
      />
      {/* Header */}
      <div ref={headerRef} className="pt-36 sm:pt-44 pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] text-text-primary mb-6">
            La plataforma completa para tu agencia.
          </h1>
          <p className="text-xl sm:text-2xl text-text-secondary font-medium leading-snug mb-10">
            MEC integra todas las herramientas que tu agencia necesita:
            desde el dashboard operativo hasta las apps móviles para clientes y repartidores.
          </p>
          <Button
            onClick={() => navigate('/plataforma')}
            className="rounded-full bg-brand text-white px-7 h-12 text-[17px] hover:bg-brand-deep transition-colors group"
          >
            Solicitar una demo
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>

      {/* Services Grid */}
      <div ref={servicesRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card bg-white rounded-[28px] shadow-card p-8"
            >
              <div className="w-14 h-14 rounded-2xl bg-brand flex items-center justify-center mb-6">
                <service.icon className="w-7 h-7 text-white" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">
                {service.title}
              </h2>
              <p className="text-text-secondary mb-6">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-sm text-text-secondary"
                  >
                    <CheckCircle className="w-4 h-4 text-brand flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Features */}
      <div ref={featuresRef} className="pb-24 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary text-center mb-10">
            Características adicionales
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-item bg-white rounded-[24px] shadow-card p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
